/**
 * Confirmación modal.
 *
 * Un `<dialog>` nativo abierto con `showModal()`, no un `<div>` con fondo propio.
 * El patrón viene del cajón del panel de administración, donde está el motivo
 * escrito: la plataforma da modalidad, Escape, contención del foco y capa superior
 * gratis, y ninguna de esas cuatro puede desincronizarse de la CSS. Escrito aquí una
 * vez para que el segundo sitio que confirme algo no vuelva a decidirlo.
 */

import { useEffect, useRef } from "react";

export function ConfirmDialog({
  title,
  children,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    opener.current = document.activeElement as HTMLElement | null;
    if (dialog !== null && !dialog.open) dialog.showModal();
    // Devolver el foco al botón que abrió esto es lo único que la plataforma no
    // hace por su cuenta: React desmonta el diálogo cuando el padre limpia su
    // estado, así que el elemento desaparece con el diálogo todavía abierto y el
    // foco cae a `<body>`.
    return () => {
      opener.current?.focus?.();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className="confirm"
      aria-labelledby="confirm-title"
      onCancel={onCancel}
      onClose={onCancel}
      // El fondo es `::backdrop`, que no es un hijo, así que un clic fuera se
      // detecta porque aterriza en la caja del propio diálogo.
      onClick={(event) => {
        if (event.target === ref.current) onCancel();
      }}
    >
      <h2 className="confirm-title" id="confirm-title">
        {title}
      </h2>
      <div className="confirm-body">{children}</div>
      <div className="confirm-foot">
        <button type="button" className="btn btn--quiet" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </button>
        <button type="button" className="btn btn--primary" onClick={onConfirm} disabled={busy}>
          {busy ? "Guardando…" : confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
